

import { useState, useRef, useEffect } from "react"
import {
  X,
  FileText,
  ImageIcon,
  FileArchive,
  FileCode,
  FilePlus,
  Upload,
  FileSpreadsheet,
  FileIcon as FileWord,
  FileIcon as FilePdf,
} from "lucide-react"

// Import both processors
import { processFiles as processFilesAdvanced } from "../utils/fileProcessor"
import { processFiles as processFilesSimple } from "../utils/simpleFileProcessor"

// Update the component to accept the fileInputRef prop
const FileUploadHandler = ({
  onFilesAdded,
  onFileRemoved,
  maxFiles = 5,
  acceptedFileTypes = "*",
  fileInputRef: externalFileInputRef, // Accept external ref
}) => {
  const [files, setFiles] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isProcessing, setIsProcessing] = useState(false)
  const internalFileInputRef = useRef(null)
  const dropAreaRef = useRef(null)

  // Use the external ref if provided, otherwise use the internal one
  const fileInputRef = externalFileInputRef || internalFileInputRef

  // Clear error message after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("")
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [errorMessage])

  // Process files with fallback
  const processFilesWithFallback = async (filesToProcess) => {
    try {
      console.log("Attempting to process files with advanced processor...")
      // Try the advanced processor first
      return await processFilesAdvanced(filesToProcess)
    } catch (error) {
      console.warn("Advanced file processing failed, using simple processor:", error)

      try {
        // Fall back to the simple processor
        console.log("Falling back to simple processor...")
        return await processFilesSimple(filesToProcess)
      } catch (fallbackError) {
        console.error("Both processors failed:", fallbackError)

        // Last resort: create basic file info objects
        console.log("Creating basic file info as last resort...")
        return filesToProcess.map((file) => ({
          type: file.type.startsWith("image/") ? "image" : "unknown",
          content: file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : `[File ${file.name} could not be processed]`,
          name: file.name,
        }))
      }
    }
  }

  // Handle file selection
  const handleFileSelection = async (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return

    // Check if adding these files would exceed the limit
    if (files.length + selectedFiles.length > maxFiles) {
      setErrorMessage(`Maximum of ${maxFiles} files allowed`)
      return
    }

    setIsProcessing(true)

    // Process each file
    const newFiles = Array.from(selectedFiles).map((file) => {
      return {
        id: `file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        file: file,
        name: file.name,
        type: file.type || getFileTypeFromExtension(file.name),
        size: file.size,
        preview: getFilePreview(file),
      }
    })

    const updatedFiles = [...files, ...newFiles]
    setFiles(updatedFiles)

    // Process the files and notify parent component
    try {
      const processedFiles = await processFilesWithFallback(newFiles.map((f) => f.file))

      if (onFilesAdded) {
        onFilesAdded(newFiles, updatedFiles, processedFiles)
      }
    } catch (error) {
      console.error("Error processing files:", error)
      setErrorMessage("Error processing files. Some features may be limited.")

      // Still notify parent with the files, even if processing failed
      if (onFilesAdded) {
        onFilesAdded(newFiles, updatedFiles, [])
      }
    } finally {
      setIsProcessing(false)
    }
  }

  // Get file type from extension if MIME type is not available
  const getFileTypeFromExtension = (filename) => {
    const ext = filename.split(".").pop().toLowerCase()

    const mimeTypes = {
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      txt: "text/plain",
      csv: "text/csv",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      svg: "image/svg+xml",
    }

    return mimeTypes[ext] || "application/octet-stream"
  }

  // Generate appropriate preview based on file type
  const getFilePreview = (file) => {
    if (file.type.startsWith("image/")) {
      return URL.createObjectURL(file)
    }
    return null
  }

  // Handle file input change
  const handleFileInputChange = (event) => {
    handleFileSelection(event.target.files)
    // Reset the input value to allow selecting the same file again
    event.target.value = ""
  }

  // Handle click on the drop area
  const handleDropAreaClick = () => {
    fileInputRef.current?.click()
  }

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDragging) setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFiles = e.dataTransfer.files
    handleFileSelection(droppedFiles)
  }

  // Handle paste event
  const handlePaste = (e) => {
    const items = e.clipboardData.items
    const pastedFiles = []

    for (const item of items) {
      // Check if the item is a file
      if (item.kind === "file") {
        const file = item.getAsFile()
        if (file) {
          pastedFiles.push(file)
        }
      }
    }

    if (pastedFiles.length > 0) {
      handleFileSelection(pastedFiles)
    }
  }

  // Remove a file
  const removeFile = (fileId) => {
    const updatedFiles = files.filter((file) => file.id !== fileId)
    setFiles(updatedFiles)

    // Notify parent component
    if (onFileRemoved) {
      onFileRemoved(fileId, updatedFiles)
    }
  }

  // Remove all files
  const removeAllFiles = () => {
    setFiles([])
    if (onFileRemoved) {
      onFileRemoved(null, [])
    }
  }

  // Get appropriate icon based on file type
  const getFileIcon = (fileType, fileName) => {
    // Check file extension if type is not specific enough
    const extension = fileName.split(".").pop().toLowerCase()

    if (fileType.startsWith("image/")) {
      return <ImageIcon className="h-6 w-6 text-blue-500" />
    } else if (fileType.startsWith("application/pdf") || extension === "pdf") {
      return <FilePdf className="h-6 w-6 text-red-500" />
    } else if (
      fileType.includes("spreadsheet") ||
      fileType.includes("excel") ||
      extension === "xlsx" ||
      extension === "xls"
    ) {
      return <FileSpreadsheet className="h-6 w-6 text-green-500" />
    } else if (
      fileType.includes("document") ||
      fileType.includes("word") ||
      extension === "docx" ||
      extension === "doc"
    ) {
      return <FileWord className="h-6 w-6 text-blue-700" />
    } else if (fileType.startsWith("text/")) {
      return <FileText className="h-6 w-6 text-gray-500" />
    } else if (fileType.startsWith("application/zip") || fileType.startsWith("application/x-")) {
      return <FileArchive className="h-6 w-6 text-yellow-500" />
    } else if (
      fileType.startsWith("application/json") ||
      fileType.includes("javascript") ||
      fileType.includes("code")
    ) {
      return <FileCode className="h-6 w-6 text-purple-500" />
    } else {
      return <FilePlus className="h-6 w-6 text-gray-500" />
    }
  }

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  const handleFilesAdded = async (newFiles, allFiles) => {
    setFiles(allFiles)

    // Automatically process files when they're added
    processNewFiles(newFiles.map((f) => f.file))
  }

  const processNewFiles = async (filesToProcess) => {
    if (filesToProcess.length === 0) return

    setIsProcessing(true)
    try {
      // Try the advanced processor first
      const processed = await processFilesAdvanced(filesToProcess)
      // setProcessedContents((prev) => [...prev, ...processed])
    } catch (error) {
      console.warn("Advanced file processing failed, using simple processor:", error)

      // Fall back to the simple processor
      try {
        const processed = await processFilesSimple(filesToProcess)
        // setProcessedContents((prev) => [...prev, ...processed])
      } catch (fallbackError) {
        console.error("Both processors failed:", fallbackError)
        setErrorMessage("Error processing files. Some features may be limited.")

        // Still notify parent with the files, even if processing failed
      }
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="w-full relative">
      {/* Error message */}
      {errorMessage && (
        <div className="mb-2 p-2 bg-red-100 text-red-700 rounded-md text-sm flex items-center">
          <span className="mr-2">⚠️</span>
          {errorMessage}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedFileTypes}
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Processing indicator */}
      {isProcessing && (
        <div className="absolute top-2 right-2 z-20 p-2 bg-blue-50 text-blue-700 rounded-md text-sm flex items-center justify-center">
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-700"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Processing...
        </div>
      )}

      {/* File list - only show if files are present */}
      {files.length > 0 && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-medium text-gray-700">Attached Files ({files.length})</h3>
            {files.length > 1 && (
              <button onClick={removeAllFiles} className="text-xs text-red-600 hover:text-red-800 transition-colors">
                Remove All
              </button>
            )}
          </div>

          <div className="space-y-2 max-h-60 overflow-y-auto p-1">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center overflow-hidden">
                  {file.preview ? (
                    <img
                      src={file.preview || "/placeholder.svg"}
                      alt={file.name}
                      className="h-10 w-10 object-cover rounded mr-3 border border-gray-200"
                    />
                  ) : (
                    <div className="h-10 w-10 flex items-center justify-center rounded mr-3 bg-gray-100">
                      {getFileIcon(file.type, file.name)}
                    </div>
                  )}
                  <div className="overflow-hidden">
                    <p className="text-sm font-medium text-gray-800 truncate" title={file.name}>
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(file.id)}
                  className="ml-2 p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100 transition-colors"
                  title="Remove file"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* This is the invisible drop area that covers the entire input area */}
      <div
        ref={dropAreaRef}
        className={`absolute inset-0 z-10 pointer-events-none ${
          isDragging
            ? "pointer-events-auto bg-blue-50 bg-opacity-90 border-2 border-dashed border-blue-500 rounded-xl"
            : ""
        }`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg shadow-lg text-center border-2 border-blue-500">
              <Upload className="h-12 w-12 text-blue-500 mx-auto mb-3" />
              <p className="text-lg font-medium text-gray-700 mb-1">Drop files here to upload</p>
              <p className="text-sm text-gray-500">Supports images, PDFs, documents, and more</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FileUploadHandler
